import { z } from 'zod'

export const TASK_STATUSES = ['todo', 'in-progress', 'waiting', 'done'] as const

export const TASK_CATEGORIES = [
  'basic',
  'venue',
  'guests',
  'budget',
  'suppliers',
  'style',
  'day',
  'other',
] as const

export const TASK_PRIORITIES = ['low', 'normal', 'high'] as const

export const taskStatusSchema = z.enum(TASK_STATUSES)
export const taskCategorySchema = z.enum(TASK_CATEGORIES)
export const taskPrioritySchema = z.enum(TASK_PRIORITIES)

const optionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
    z.string().trim().max(max).optional(),
  )

const clearableOptionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
    z.string().trim().max(max).nullable().optional(),
  )

const optionalDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.iso.date().optional(),
)

const clearableOptionalDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.iso.date().nullable().optional(),
)

const assigneeMemberIdSchema = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.string().trim().min(1).nullable(),
)

export const createPlanningTaskSchema = z.object({
  title: z.string().trim().min(1, 'Enter task title').max(180),
  description: optionalText(500),
  category: taskCategorySchema.default('other'),
  status: taskStatusSchema.default('todo'),
  priority: taskPrioritySchema.default('normal'),
  dueDate: optionalDate,
  assigneeMemberId: assigneeMemberIdSchema.default(null),
  notes: optionalText(3000),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
})

export const updatePlanningTaskSchema = z.object({
  title: z.string().trim().min(1, 'Enter task title').max(180).optional(),
  description: clearableOptionalText(500),
  category: taskCategorySchema.optional(),
  status: taskStatusSchema.optional(),
  priority: taskPrioritySchema.optional(),
  dueDate: clearableOptionalDate,
  assigneeMemberId: assigneeMemberIdSchema.optional(),
  notes: clearableOptionalText(3000),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
})

export type TaskStatus = (typeof TASK_STATUSES)[number]
export type TaskCategory = (typeof TASK_CATEGORIES)[number]
export type TaskPriority = (typeof TASK_PRIORITIES)[number]
export type CreatePlanningTaskInput = z.infer<typeof createPlanningTaskSchema>
export type UpdatePlanningTaskInput = z.infer<typeof updatePlanningTaskSchema>
